import { useMemo } from 'react';
import { useT } from '@/store';
import { useDashboard, useDwellStatus } from '@/hooks/useTenant';
import { useActiveOrders, useLiveEta } from '@/hooks/useOrders';
import { useDriverMap } from '@/hooks/useDirectory';
import { Panel, EmptyState, SkeletonRows } from '@/components/ui/Panel';
import { StatusBadge, DwellBadge, NotNotifiedBadge } from '@/components/ui/StatusBadge';
import { STAGES, waybillLabel } from '@/lib/format';
import { deliveryWindow } from '@/lib/deliveryWindow';

/**
 * The dispatcher's "what is happening right now" strip: counters across the
 * top, one progress row per in-flight order below.
 *
 * The progress percentage is stage-based (checkpoints along dispatched →
 * loading → en_route → unloading → delivered) rather than distance-based.
 * That is deliberate and matches the monolith: a GPS-derived percentage
 * jumps backwards whenever a truck takes a detour, and a bar that goes
 * backwards is read as a bug rather than as information.
 */
export function ActiveFleetBar({ onQuickDispatch }) {
  const t = useT();
  const { data: dashboard, isLoading } = useDashboard();
  const { data: activeOrders = [] } = useActiveOrders();
  const { data: dwell } = useDwellStatus();
  const driversById = useDriverMap();
  const dwellByOrder = dwell?.byOrder ?? {};
  // Realaus laiko ETA. Its own poll, deliberately — see useLiveEta().
  const { data: etaByOrder = {} } = useLiveEta();

  const counters = useMemo(
    () => [
      { key: 'dispatcher.activeTrucks', value: dashboard?.active },
      { key: 'status.loading', value: dashboard?.loading },
      { key: 'status.en_route', value: dashboard?.en_route },
      { key: 'dispatcher.deliveredToday', value: dashboard?.delivered_today },
    ],
    [dashboard],
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {counters.map((c) => (
          <div key={c.key} className="panel !p-3.5">
            <div className="text-[10px] font-bold uppercase tracking-[0.03em] text-dim">{t(c.key)}</div>
            <div className="font-brand text-[26px] font-bold leading-tight">
              {isLoading ? '—' : (c.value ?? 0)}
            </div>
          </div>
        ))}
      </div>

      <Panel
        title={t('dispatcher.liveTracking')}
        actions={
          <button type="button" className="btn btn-primary" onClick={onQuickDispatch}>
            {t('orders.newOrderBtn')}
          </button>
        }
      >
        {isLoading ? (
          <SkeletonRows rows={4} />
        ) : activeOrders.length === 0 ? (
          <EmptyState>{t('dispatcher.noActiveOrders')}</EmptyState>
        ) : (
          <ul className="max-h-[380px] space-y-2.5 overflow-y-auto pr-1">
            {activeOrders.map((order) => (
              <ActiveOrderRow
                key={order.order_id}
                order={order}
                driver={driversById[order.driver_id]}
                dwellEntry={dwellByOrder[order.order_id]}
                eta={etaByOrder[order.order_id]}
              />
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

/**
 * Realaus laiko ETA dispečerio sąraše — when this truck reaches its NEXT
 * stop, and how much that number can be trusted.
 *
 * The client portal has shown a routed ETA to the paying client since the
 * OSRM work, and a truck-legal one through Valhalla since V-302. This strip
 * — the screen the person who ANSWERS that client's phone call actually
 * looks at — had a stage bar and no time at all.
 *
 * WHICH STOP, and it is not always the site. A 'dispatched' truck is
 * driving to the quarry to load; the portal never asks that question
 * because a client only cares when the load reaches them. So the
 * destination name is rendered next to the time rather than assumed —
 * "Draseikiai 11:42" and "Objektas Nr. 4 11:42" are different facts and a
 * dispatcher must never have to guess which one this is.
 *
 * The eta_source marker is not decoration. It is the same "never present an
 * estimate as measured" rule the client portal follows on the same numbers:
 *   routed_truck — Valhalla answered for this load's actual truck
 *   routed       — OSRM answered, car-shaped
 *   estimated    — straight line; nothing was routed
 *   stale        — computed from a GPS fix too old to place a truck by
 * Only the two soft ones are marked, and `stale` is the only one coloured:
 * an estimate is a weaker number, a stale fix is a number about a truck
 * that may not be where this says it is.
 *
 * Renders NOTHING when there is no ETA to give — a truck parked at the
 * quarry, a driver who has never pinged, a routing service that is down.
 * A dash in this slot would read as a value somebody forgot to fill in.
 */
/**
 * The one warning on this strip that is about the FUTURE.
 *
 * Every other marker here reports something that has already happened — a
 * stop nobody closed, a driver never told, a window already missed. This
 * fires while the window is still open and the live ETA says the truck will
 * not make it, which is the only moment a dispatcher can still do anything:
 * reassign, reorder the day, or ring the client while it is news rather than
 * an apology.
 *
 * Red, and above the ETA line rather than inside it: a predicted breach of a
 * promise to a paying client outranks the arrival time it is derived from.
 * The forecast's own honesty marker rides along when the number behind it is
 * a straight-line estimate — see willMiss() for why a stale fix is not
 * allowed to raise this alarm at all.
 */
function WillMissLine({ order, eta }) {
  const t = useT();
  const window = deliveryWindow(order, new Date(), eta);
  if (window?.state !== 'will_miss') return null;

  return (
    <p className="mt-1 text-[10.5px] font-semibold text-[#FCA5A5]">
      {t('dispatcher.willMiss', { n: window.minutesLate })}
      {window.etaSource === 'estimated' ? ` ${t('dispatcher.etaSourceShort.estimated')}` : ''}
    </p>
  );
}

function EtaLine({ eta }) {
  const t = useT();
  if (!eta || eta.eta_minutes === null || eta.eta_minutes === undefined) return null;

  const clock = eta.eta_at
    ? new Date(eta.eta_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;
  const soft = eta.eta_source === 'stale' || eta.eta_source === 'estimated';

  return (
    <span
      className={`text-[10.5px] ${eta.eta_source === 'stale' ? 'text-amber' : 'text-dim'}`}
      title={soft ? t(`dispatcher.etaSource.${eta.eta_source}`) : undefined}
    >
      {t('dispatcher.etaTo', {
        where: eta.destination_name ?? t(`dispatcher.etaDest.${eta.destination}`),
        clock: clock ?? '—',
        n: eta.eta_minutes,
      })}
      {soft ? ` ${t(`dispatcher.etaSourceShort.${eta.eta_source}`)}` : ''}
    </span>
  );
}

function ActiveOrderRow({ order, driver, dwellEntry, eta }) {
  const t = useT();
  const stageIndex = STAGES.indexOf(order.order_status);
  // Half-open interval: a truck that has just entered a stage is partway
  // through it, not at its start line — the monolith's same convention.
  const progress = stageIndex < 0 ? 0 : ((stageIndex + 0.5) / STAGES.length) * 100;

  return (
    <li className="rounded-[11px] border border-line bg-white/2 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-[12.5px] font-semibold">{waybillLabel(order)}</div>
          <div className="truncate text-[11px] text-dim">
            {[order.quarry_name, order.site_name].filter(Boolean).join(' → ') || '—'}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <StatusBadge status={order.order_status} live />
          <DwellBadge entry={dwellEntry} />
          {/* This strip is where a dispatcher actually looks, so it is
              where "the driver never heard about this load" has to appear
              — a row here that never leaves 'dispatched' is exactly the
              shape of a truck that is not moving. */}
          <NotNotifiedBadge order={order} />
        </div>
      </div>

      <WillMissLine order={order} eta={eta} />

      <div className="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-white/6">
        <div
          className="h-full rounded-full bg-gradient-to-r from-accent-solid to-accent-hi transition-[width] duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="mt-1.5 flex flex-wrap items-baseline justify-between gap-x-2 text-[10.5px] text-dim">
        <span>{driver?.full_name ?? t('dispatcher.unassigned')}</span>
        {/* Between the driver and the tonnage, on the line a dispatcher
            already reads for this row — not a column of its own, which
            would be empty on every truck standing at a weighbridge. */}
        <EtaLine eta={eta} />
        <span>
          {order.actual_ocr_weight_tons
            ? `${order.actual_ocr_weight_tons} t`
            : `${order.estimated_weight_tons ?? '—'} t ${t('common.estSuffix')}`}
        </span>
      </div>
    </li>
  );
}
